import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Col, Grid, Row, theme } from 'antd';
import type { MonetizationCustomerDetailOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { CustomerBillingPeriodsTable } from '@/components/monetization/customers/CustomerBillingPeriodsTable';
import { CustomerEntitlementsTable } from '@/components/monetization/customers/CustomerEntitlementsTable';
import { CustomerEventsTable } from '@/components/monetization/customers/CustomerEventsTable';
import { CustomerRevenueCatCard } from '@/components/monetization/customers/CustomerRevenueCatCard';
import { CustomerWithdrawalContractsTable } from '@/components/monetization/customers/CustomerWithdrawalContractsTable';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import { CUSTOMER_DEFAULT_TAB, CUSTOMER_SHOW_TABS } from '../constants';
import { CustomerShowHeader } from './CustomerShowHeader';
import { CustomerGeneralTab } from './CustomerGeneralTab';

export const CustomerShow = () => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const { query } = useShow<MonetizationCustomerDetailOut>({
    resource: 'monetization.customers',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  useRecordDocumentTitle(
    'monetization.customers',
    record?.owner_email ?? record?.revenuecat_customer_id
  );

  const [activeTab, onTabChange] = useShowTab(
    CUSTOMER_SHOW_TABS,
    CUSTOMER_DEFAULT_TAB
  );

  return (
    <Show isLoading={isLoading} contentProps={bareShowContentProps}>
      {record && (
        <>
          <CustomerShowHeader
            record={record}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
          <Row
            gutter={[token.margin, token.margin]}
            style={{ marginTop: token.margin }}
          >
            <Col flex="1 1 480px" style={{ minWidth: 0 }}>
              {activeTab === 'general' && (
                <CustomerGeneralTab record={record} />
              )}
              {activeTab === 'entitlements' && (
                <CustomerEntitlementsTable accountId={record.account_id} />
              )}
              {activeTab === 'billing_periods' && (
                <CustomerBillingPeriodsTable accountId={record.account_id} />
              )}
              {activeTab === 'events' && (
                <CustomerEventsTable accountId={record.account_id} />
              )}
              {activeTab === 'withdrawal_contracts' && (
                <CustomerWithdrawalContractsTable
                  accountId={record.account_id}
                />
              )}
            </Col>
            {activeTab === 'general' && (
              <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
                <CustomerRevenueCatCard record={record} />
              </Col>
            )}
          </Row>
        </>
      )}
    </Show>
  );
};
